import {
  isPromoBasisPoints,
  promoEnded,
  promoOffered,
  promoPriceCents,
  type PromoWindow,
} from '@barso/shared';
import type { CartLine, SnapshotPromoJson } from './types';

/**
 * Rabattaktion an der Kasse (X7, Plan 08). Die Aktionen kommen mit dem Snapshot (Meta-Schlüssel `promos`), gestartet wird von
 * Hand (`promoActive`). Gerechnet wird nur mit `promoPriceCents` aus `@barso/shared`: Kasse und Server kommen auch offline zum
 * selben Preis. Es gibt keinen freien Rabatt, nur eine Aktion aus dem Snapshot lässt sich anwenden.
 */

export interface PromoView {
  id: string;
  basisPoints: number;
  startsAtMs: number;
  endsAtMs: number | null;
  cancelled: boolean;
}

/** Zeitraum für die Prüfung aus `@barso/shared`: bei einer Absage steht `endsAt` für den Zeitpunkt der Absage. */
export function windowOf(promo: PromoView): PromoWindow {
  return {
    startsAtMs: promo.startsAtMs,
    endsAtMs: promo.endsAtMs,
    cancelledAtMs: promo.cancelled ? (promo.endsAtMs ?? promo.startsAtMs) : null,
  };
}

/** Übernimmt die Aktionen des Snapshots; Unbrauchbares (kaputte Zeit, unzulässiger Rabatt) wird übergangen, nie geraten. */
export function toPromoViews(promos: readonly SnapshotPromoJson[]): PromoView[] {
  const views: PromoView[] = [];
  for (const promo of promos) {
    const startsAtMs = Date.parse(promo.startsAt);
    const endsAtMs = promo.endsAt === null ? null : Date.parse(promo.endsAt);
    if (Number.isNaN(startsAtMs) || (endsAtMs !== null && Number.isNaN(endsAtMs))) continue;
    if (!isPromoBasisPoints(promo.basisPoints)) continue;
    views.push({
      id: promo.id,
      basisPoints: promo.basisPoints,
      startsAtMs,
      endsAtMs,
      cancelled: promo.cancelled,
    });
  }
  return views;
}

/**
 * Aktion, die das Banner zum Start anbietet: im Zeitraum (Gerätezeit, Toleranz ±15 Minuten), nicht abgesagt. Nur bei einem
 * Server, der Aktionen kennt (`capable`). Offline-Kassen ohne aktuellen Snapshot kennen die Aktion nicht (der sichere Fehler:
 * voller Preis).
 */
export function offeredPromo(
  promos: readonly PromoView[],
  capable: boolean,
  nowMs: number,
): PromoView | null {
  if (!capable) return null;
  return promos.find((promo) => promoOffered(windowOf(promo), nowMs)) ?? null;
}

/**
 * Aktion, die gerade an dieser Kasse läuft: von Hand gestartet (`activeId`), im Snapshot bekannt und weder beendet noch
 * abgesagt. Ende laut Snapshot erreicht: `null` (die Kasse schaltet sich dann selbst aus).
 */
export function runningPromo(
  promos: readonly PromoView[],
  capable: boolean,
  activeId: string | null,
  nowMs: number,
): PromoView | null {
  if (!capable || activeId === null) return null;
  const promo = promos.find((entry) => entry.id === activeId);
  if (promo === undefined || promoEnded(windowOf(promo), nowMs)) return null;
  return promo;
}

/**
 * Preis einer Zeile unter der laufenden Aktion. Nur Artikel mit Einverständnis (`discountAllowed`) und bekanntem Artikel
 * bekommen den Aktionspreis; alle anderen behalten den vollen Preis. Ohne Aktion kehrt die Zeile zum Listenpreis zurück.
 * Reine Funktion: Ein- und Ausschalten rechnet die Zeilen immer wieder vom Listenpreis aus.
 */
export function priceLine(line: CartLine, active: PromoView | null): CartLine {
  // Eine Zeile mit Preiskorrektur (U17g) behält ihren korrigierten Preis: Korrektur und Aktion gibt es nie zugleich
  if (line.priceChange !== undefined) return line;
  if (line.itemId === null || line.discountAllowed !== true) return line;
  const listPriceCents = line.promo?.listPriceCents ?? line.priceCents;
  if (active === null) {
    if (line.promo === null || line.promo === undefined) return line;
    return { ...line, priceCents: listPriceCents, promo: null };
  }
  return {
    ...line,
    priceCents: promoPriceCents(listPriceCents, active.basisPoints),
    promo: { id: active.id, basisPoints: active.basisPoints, listPriceCents },
  };
}

/** Alle Zeilen neu berechnen (beim Ein- und Ausschalten, nur vor dem Bezahlen). */
export function repriceCart(cart: readonly CartLine[], active: PromoView | null): CartLine[] {
  return cart.map((line) => priceLine(line, active));
}

/** Hat die Zeile tatsächlich einen niedrigeren Preis wegen der Aktion? (Anzeige „~~4,00~~ 2,00 € −50 %“.) */
export function isReduced(line: CartLine): boolean {
  return (
    line.promo !== null && line.promo !== undefined && line.priceCents < line.promo.listPriceCents
  );
}

/**
 * `promoId` für den Verkauf: nur, wenn der Preis genau dem Aktionspreis entspricht (sonst würde der Server eine
 * Abweichung melden, die es nicht gibt) und der Server Aktionen kennt. Sonst `undefined`.
 */
export function promoIdFor(line: CartLine, capable: boolean): string | undefined {
  if (!capable || line.promo === null || line.promo === undefined) return undefined;
  if (!isPromoBasisPoints(line.promo.basisPoints)) return undefined;
  if (promoPriceCents(line.promo.listPriceCents, line.promo.basisPoints) !== line.priceCents) {
    return undefined;
  }
  return line.promo.id;
}

/** Zustand der Aktion an dieser Kasse, wie er im lokalen Speicher liegt. */
export interface PromoState {
  promos: PromoView[];
  /** Der zuletzt geladene Snapshot enthielt `promos`: Der Server kennt `promoId`. */
  capable: boolean;
  /** Von Hand gestartete Aktion dieser Kasse. */
  activeId: string | null;
}

export const NO_PROMO_STATE: PromoState = { promos: [], capable: false, activeId: null };
